/**
 * PersonalizationNudge — Adaptive nudge card with analytics tracking
 * 
 * Receives all config from useAdaptiveHome hook.
 * Tracks: impression (via parent), click, dismiss.
 */
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check } from 'lucide-react';
import { uxLog } from '@/services/homeUxLogger';
import { homeAnalytics } from '@/services/homeAnalytics';

const ACCENT_COLORS = {
  indigo:  { bg: 'rgba(99,102,241,0.06)',  border: 'rgba(99,102,241,0.12)', text: '#a5b4fc', glow: 'rgba(99,102,241,0.08)' },
  purple:  { bg: 'rgba(139,92,246,0.06)',   border: 'rgba(139,92,246,0.12)', text: '#c4b5fd', glow: 'rgba(139,92,246,0.08)' },
  blue:    { bg: 'rgba(59,130,246,0.06)',    border: 'rgba(59,130,246,0.12)', text: '#93c5fd', glow: 'rgba(59,130,246,0.08)' },
  emerald: { bg: 'rgba(16,185,129,0.06)',    border: 'rgba(16,185,129,0.12)', text: '#6ee7b7', glow: 'rgba(16,185,129,0.08)' },
  violet:  { bg: 'rgba(139,92,246,0.06)',    border: 'rgba(139,92,246,0.12)', text: '#c4b5fd', glow: 'rgba(139,92,246,0.08)' },
};

const PersonalizationNudge = ({ nudge, glow, animation, fullState, isSimulation }) => {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (!nudge || nudge.completed) return;

    if (fullState) {
      uxLog.click(fullState, isSimulation);
      homeAnalytics.click(fullState, isSimulation);
      uxLog.personalizationStart(fullState, isSimulation);
      homeAnalytics.personalizationStart(fullState, isSimulation);
    }

    navigate('/hearing-knowledge');
  }, [nudge, fullState, isSimulation, navigate]);

  if (!nudge) return null;

  const colors = ACCENT_COLORS[nudge.accent] || ACCENT_COLORS.indigo;
  const isCompleted = !!nudge.completed;
  const pulseDuration = animation?.pulse || '3s';
  const boxGlow = glow?.hue || colors.glow;

  return (
    <div
      className="w-full mt-6"
      style={{ animation: 'nudgeFadeIn 0.6s ease-out both' }}
      data-testid="personalization-nudge"
    >
      {/* Simulation badge — only visible when sim is active */}
      {isSimulation && (
        <div
          className="mb-2 px-3 py-1 rounded-full text-[10px] font-mono tracking-wider w-fit"
          style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)' }}
          data-testid="simulation-badge"
        >
          SIMULATION ACTIVE
        </div>
      )}

      <button
        onClick={handleClick}
        className={`group relative w-full px-5 py-4 rounded-2xl text-left transition-all overflow-hidden ${isCompleted ? 'cursor-default' : 'active:scale-[0.98]'}`}
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          boxShadow: `0 0 30px ${boxGlow}`,
        }}
        data-testid="personalization-nudge-button"
      >
        {/* Subtle pulse glow */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 50% 50%, ${boxGlow}, transparent 70%)`,
            animation: `nudgePulse ${pulseDuration} ease-in-out infinite`,
            opacity: 0.3,
          }}
        />

        <div className="relative flex items-start gap-3">
          {isCompleted ? (
            <div
              className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
              style={{ background: colors.border }}
            >
              <Check size={12} style={{ color: colors.text }} />
            </div>
          ) : (
            <Sparkles size={14} style={{ color: 'rgba(255,255,255,0.38)' }} className="mt-0.5 flex-shrink-0" />
          )}

          <div className="flex-1 min-w-0">
            <p className="text-white/72 text-[13px] font-normal leading-relaxed">
              {nudge.title}
            </p>

  {nudge.subtitle && (
  <div className="mt-3 pt-3 border-t border-white/8">
    <p className="text-white/58 text-[12px] leading-relaxed">
      {nudge.subtitle}
    </p>
  </div>
)}

{nudge.cta && !isCompleted && (
  <p className="mt-2 text-[11px] text-white/35 leading-none transition-colors duration-300 group-hover:text-white/50">
    {nudge.cta}
  </p>
)}
</div>
        </div>
      </button>
    </div>
  );
};

export default PersonalizationNudge;
